import IndexStore from 'src/stores';
import type {ReduxStore} from 'src/reducers';
import * as React from 'react';
import {useSelector} from 'react-redux';
import {useRoutes, useHistory} from 'src/rerouter';
import {
  useTRMEnabled,
  useReleaseFlag,
  useCrmMailbox,
  useTalentPoolEnabled,
  useAgentBuilderEnabled,
  useDataVariablePageV2Enabled,
  useTaskAndReminderEnabled,
  useByodEnabled,
  useAIReceptionistEnabled,
} from 'src/hooks/product-flags';
import {useCRMMigrationEnabled} from 'src/hooks/useCrm';
import {workflowJourneyRedirect} from './workflow-journey-redirect';
import {container, redirect, split, onEnter, imbue} from './utils';
import usePrivilege from 'src/hooks/usePrivilege';
import useAgencyConfig, {
  useSenseEvents,
  useEnableVoiceflowPage,
  useVoiceAiMarketingPageEnabled,
  useAutomationSurvey,
  useChatbotEnabled,
  useAiRecruiterV2,
} from 'src/hooks/useAgencyConfig';
import {useAgencyMaintenanceMode} from 'src/hooks/useAgencyData';
import useDisplayConfig, {useJ2Enabled} from 'src/hooks/useDisplayConfig';
import {signOut} from 'src/actions/index';
import {isMobileDevice} from 'src/utils/responsive';
import {
  selectCurrentAccount,
  getAccountPrivileges,
} from 'src/selectors/accounts';
import {AnalyticsService} from 'src/analytics';
import {RouterErrorBoundary} from 'src/components/500/boundary';
import ExtensionConnection from 'src/components/micro-apps-extension/extension-connection';
import AppShell from 'src/components/app-shell/app-shell-container';
import {Dashboard as CrmDashboard} from 'src/components/crm/dashboard/dashboard';
import {CandidatesInOfferStage} from 'src/components/crm/dashboard/candidates-in-offer-stage/candidates-in-offer-stage';
import {CandidatesToJoin} from 'src/components/crm/dashboard/candidates-to-join/candidates-to-join';
import DashboardContainer from 'src/components/dashboard/dashboard-container';
import DashboardV2 from 'src/components/dashboard/dashboard-v2';
import EmptyDashboard from 'src/components/dashboard/empty-dashboard';
import TemplateDetail from 'src/components/settings/template/container';
import StyleGuide from 'src/components/style-guide/index';
import NotFound from 'src/components/404/404';
import NotAllowed from 'src/components/500/not-allowed';
import Error500 from 'src/components/500/500';
import DeviceNotSupported from 'src/components/500/device-not-supported';
import DesignDoc from 'src/components/design-doc';
import OnboardPage from 'src/components/agency-onboard/onboard-page/onboard-page';
import {MarkTaskCompletion} from 'src/components/agency-onboard/mark-task-completion/mark-task-completion';
import {MarkOnboardingCompletion} from 'src/components/agency-onboard/mark-onboarding-completion/mark-onboarding-completion';
import SenseJDAi from 'src/components/sense-jd-ai/sense-ai';
import AuthenticationContainer from 'src/components/authentication/container/container';
import SignInContainer from 'src/components/authentication/signin/signin-container';
import ForgotPasswordContainer from 'src/components/authentication/forgot/forgot-container';
import AuthenticatorAppResetApprove from 'src/components/authentication/signin/authenticator-app-registration/authenticator-app-reset-approve';
import {ApprovePhoneReset} from 'src/components/authentication/signin/phone-deregistration/phone-deregistration';
import ResetPasswordContainer from 'src/components/authentication/reset/reset-password';
import SignUpContainer from 'src/components/authentication/signup/signup-container';
import MissingLabelsContainer from 'src/components/missing-labels-dashboard/missing-labels-container';
import ReferrerPortalUserForm from 'src/components/referrer-portal/user-form/form-container';
import NotificationPanelRoute from 'src/components/notifications/notification-panel-route';
import {useDelightedRouteSurvey} from '../hooks/useDelighted';


const AtsRoutes = split(() => import('./ats'));
const SurveyRoutes = split(() => import('./survey'));
const ExecEmailRoutes = split(() => import('./exec-email'));
const RecruiterHubRoutes = split(() => import('./recruiter-hub'));
const ReferralExternalRoutes = split(() => import('./referral-external'));

const SettingsRoutes = split(() => import('./settings'));
const NewMessagesRoutes = split(() => import('./messages-new'));
const SenseAIPanelRoutes = split(() => import('./sense-ai-panel'));
const GenesisContactsRoutes = split(() => import('./contacts-genesis'));
const PeopleRoutes = split(() => import('./people'));
const JobsRoutes = split(() => import('./jobs'));
const AnalyticsGenesisRoutes = split(() => import('./analytics-genesis'));
const AuditRoutes = split(() => import('./audit'));
const AnalyticsRoutes = split(() => import('./analytics'));
const ScheduleRoutes = split(() => import('src/components/schedule/index'));
const ReferralRoutes = split(() => import('src/components/referral/referral'));
const WorkflowRoutes = split(() => import('./workflow'));
export const AutomationWorkflowRoutes = split(
  () => import('./automation-workflow'),
);
const SequenceTemplateRoutes = split(() => import('./sequence-template'));
export const AutomationTemplateRoutes = split(
  () => import('./automation-template'),
);
const AutomationSurveyRoutes = split(() => import('./automation-surveys'));
const AiRecruiterRoutes = split(() => import('./ai-recruiter'));
const AskAIRoutes = split(() => import('./ask-ai'));
const AiRecruiterV2Routes = split(() => import('./ai-recruiter-v2'));
const AIReceptionistsRoutes = split(() => import('./ai-receptionists'));
const TrmRoutes = split(() => import('./trm'));
const ChatbotRoutes = split(() => import('./chatbot'));
const DbCleanupRoutes = split(() => import('./database-cleanup'));
const SchedulerRoutes = split(() => import('./scheduler'));
const LandingPageRoutes = split(() => import('./landing-page'));
const AgentBuilderRoutes = split(
  () => import('src/components/agents/route-map'),
);

const ContentSubscriptionRoutes = split(() => import('./content-subscription'));
const ReferralV2Routes = split(() => import('./referral-v2'));
const VoiceflowRoutes = split(() => import('./voiceflow'));
const CandidateDriveRoutes = split(() => import('./drive-routes'));
const MaintenanceRoutes = split(() => import('./maintenance'));

const CrmRoutes = split(() => import('./crm'));
const CrmMailboxRoutes = split(() => import('./crm-mailbox'));
const TalentPoolsRoutes = split(() => import('./talent-pools'));
const AgenticUIPlaygroundRoutes = split(
  () => import('./agentic-ui-playground'),
);
const FieldsRoutes = split(() => import('./fields'));
const TaskAndReminderRoutes = split(() => import('./task-and-reminder'));
const ByodRoutes = split(() => import('./byod'));

const routeToProjectMap = {
  '/messages': 'Messaging',
  '/analytics/messages/outbound': 'Messaging',
  '/contacts/from/:id': 'Messaging',
} as const;

export default function AppRoutes({
  senseStore,
  reduxStore,
}: {
  senseStore: IndexStore;
  reduxStore: ReduxStore;
}) {
  const isTRMCustomer = useDisplayConfig('is_trm_customer');
  const canEditSettings = usePrivilege('EDIT_SETTINGS');
  const canViewSurveys = usePrivilege('VIEW_SURVEYS');
  const canUseMessaging = usePrivilege('USE_MESSAGING');
  const canViewAnalytics = usePrivilege('VIEW_ANALYTICS');
  const canViewAudience = usePrivilege('VIEW_AUDIENCE_MEMBERS');
  const canViewWorkflows = usePrivilege('VIEW_WORKFLOWS');
  const canViewReferralV2 = usePrivilege('VIEW_REFERRALS_V2');
  const canViewVoiceflow = useEnableVoiceflowPage();
  const canViewFieldsPage =
    useDataVariablePageV2Enabled() && !isTRMCustomer && canEditSettings;
  const canEditReferralV2 = usePrivilege('EDIT_REFERRALS_V2');
  const referralAdminV2 = usePrivilege('ADMIN_REFERRALS_V2');
  const canViewTRM = useTRMEnabled();
  const canEditChatbot = usePrivilege('EDIT_CHATBOT');
  const adminReferrals = usePrivilege('ADMIN_REFERRALS');
  const recruiterReferrals = usePrivilege('RECRUITER_REFERRALS');
  const viewAiRecruiter = usePrivilege('VIEW_AI_RECRUITER');
  const canUseReferral = adminReferrals || recruiterReferrals;
  const showSenseEvents = useSenseEvents();
  const privileges = useSelector(getAccountPrivileges) || [];
  const j2Enabled = useJ2Enabled();
  const canUsePhoneOrLatInbox = canUseMessaging || canEditChatbot;
  const canUsePhoneAndLatInbox = canUseMessaging && canEditChatbot;

  const showVoiceFlowMarketingLandingPage = useVoiceAiMarketingPageEnabled();

  // audience + MSG => For MSG phone inbox only, but if the user has LAT inbox should have audience + MSG + chatbotEdit
  const isMessagingOnly =
    canViewAudience &&
    ((privileges.length === 2 && canUsePhoneOrLatInbox) ||
      (privileges.length === 3 && canUsePhoneAndLatInbox));

  const showOnboardPage = useAgencyConfig('onboardRequired') || false;
  const aiRecruiterEnabled = useAgencyConfig('aiRecruiterEnabled');
  const askAiEnabled = Boolean(useAgencyConfig('graceEnabled'));
  const aiRecruiterV2Enabled = useAiRecruiterV2();
  const landingPageView = useDisplayConfig('landing_page_view');
  const canViewAutomationWorkflows = useDisplayConfig('aep_workflow_view');
  const isAgencyInMaintenanceMode = useAgencyMaintenanceMode();
  const canUseCrmMailbox = useCrmMailbox();
  const crmMigrationEnabled = useCRMMigrationEnabled();
  const talentPoolEnabled = useTalentPoolEnabled();
  const automationSurveyEnabled = useAutomationSurvey();
  const isAgentBuilderEnabled = useAgentBuilderEnabled();
  const isTasksEnabled = useTaskAndReminderEnabled();
  const isByodEnabled = useByodEnabled();
  const canViewScout = usePrivilege('VIEW_REPORTS_AND_DASHBOARDS');
  const canCreateScout = usePrivilege('CREATE_REPORTS_AND_DASHBOARDS');
  const canDeleteScout = usePrivilege('DELETE_REPORTS_AND_DASHBOARDS');
  const canViewByod =
    isByodEnabled && (canViewScout || canCreateScout || canDeleteScout);
  const isChatbotEnabled = useChatbotEnabled();
  const isVoiceAllowed = usePrivilege('USE_VOICEFLOW');
  const voiceEnabledAgentBuilder = useReleaseFlag('voiceEnabledAgentBuilder');
  const homepageV2Enabled = useReleaseFlag('homepageV2');
  const voiceAiV2Enabled = useAgencyConfig('voiceAiV2Enabled');
  const voiceAiEnabled = useAgencyConfig('voiceAiEnabled');
  const canAccessAgentBuilder =
    isAgentBuilderEnabled &&
    ((isChatbotEnabled && canEditChatbot) ||
      (voiceEnabledAgentBuilder &&
        (voiceAiV2Enabled || voiceAiEnabled) &&
        isVoiceAllowed));

  const userAgent = reduxStore.getState().requestHeaders.headers['user-agent'];
  const isMobile = isMobileDevice(userAgent);
  const showRecruiterHub =
    useAgencyConfig('discoverRecruiterHubV2Enabled') &&
    usePrivilege('VIEW_RECRUITER_HUB');
  const isCrmRedirectionEnabled = useReleaseFlag('crmEnabled');
  const isAIReceptionistEnabled = useAIReceptionistEnabled();

  const getIndexRedirectPath = () => {
    if (!privileges.length) {
      return '/403';
    }
    if (showOnboardPage) {
      return '/onboard';
    }
    if (isMessagingOnly || (isMobile && canUsePhoneOrLatInbox)) {
      return '/messages';
    }
    // Note(Arush): The issue was that while going from trm to dashboard,
    // it would get re-directed to trm again, hence the condition.

    if (canViewTRM && !window.location.href.includes(`dashboard`)) {
      if (isCrmRedirectionEnabled) {
        return '/dashboard';
      }
      return '/trm';
    }
    if (isMobile) {
      return '/device-not-supported';
    }
    return '/dashboard';
  };

  const indexRedirectPath = getIndexRedirectPath();

  const dynamicAgency = ['auth', 'www'].includes(
    reduxStore.getState().env.hostname.split('.')[0],
  );

  const account = selectCurrentAccount(reduxStore.getState());

  React.useEffect(() => {
    handleAnalyticsEvents();
  }, []);

  const handleAnalyticsEvents = () => {
    try {
      const state = reduxStore.getState();
      if (!state) {
        return;
      }
      const {accounts, agency, productFlags} = state;
      const userId = accounts?.authedUserId;
      const data = {
        roles: accounts.data[userId].securityRole.privileges,
        release_flags: productFlags.releaseFlags,
        agencyName: agency.agency.name,
        agency_id: agency.agency.id,
        ats_kind: agency.agency.atsSystem,
      } as const;
      AnalyticsService.identify(userId, {...data});
    } catch {}
  };

  const sideNavHiddenRoutes = [
    {
      path: 'settings/templates/:templateId',
      element: imbue(TemplateDetail),
    },
    {
      path: 'messages',
      element:
        canUseMessaging || canEditChatbot ? (
          <NewMessagesRoutes store={reduxStore} />
        ) : (
          redirect('/dashboard')
        ),
    },
    {
      path: 'sense-ai-panel',
      // @ts-expect-error - TS2322 - Type '{ store: any; }' is not assignable to type 'IntrinsicAttributes'.
      element: <SenseAIPanelRoutes store={reduxStore} />,
    },
    {
      path: 'reports',
      element: <AnalyticsGenesisRoutes />,
    },
    canUseMessaging && {
      path: 'contact(s)',
      element: <GenesisContactsRoutes store={reduxStore} />,
    },
    canUseCrmMailbox && {
      path: 'mailbox',
      element: <CrmMailboxRoutes store={reduxStore} />,
    },
    talentPoolEnabled && {
      path: 'talent-pools',
      element: <TalentPoolsRoutes store={reduxStore} />,
    },
  ];
  //TODO:(diwakersurya) fix this before releasing to prod.
  // const dashboardElement = isMobile ? (
  //   redirect(indexRedirectPath)
  // ) : canViewByod ? (
  //   <DashboardContainer />
  // ) : crmMigrationEnabled ? (
  //   <CrmDashboard />
  // ) : homepageV2Enabled ? (
  //   <DashboardV2 />
  // ) : canViewAnalytics ? (
  //   <DashboardContainer />
  // ) : (
  //   <EmptyDashboard agentName={account?.name || ''} />
  // );

  const dashboardElement =  <DashboardV2 />

  const sideNavVisibleRoutes = [
    {path: '', exact: true, element: redirect(indexRedirectPath)},
    {
      path: 'dashboard',
      children: [
        {
          path: '',
          exact: true,
          element: dashboardElement,
        },
        crmMigrationEnabled && {
          path: 'offer-candidates',
          exact: true,
          element: <CandidatesInOfferStage />,
        },
        crmMigrationEnabled && {
          path: 'candidates-to-join',
          exact: true,
          element: <CandidatesToJoin />,
        },
      ],
    },
    Boolean(showRecruiterHub) && {
      path: 'recruiter-hub-v2',
      element: <RecruiterHubRoutes />,
    },
    {path: 'notifications', element: <NotificationPanelRoute />},
    {path: 'settings', element: <SettingsRoutes />},
    {path: 'people', element: <PeopleRoutes />},
    {path: 'jobs', element: <JobsRoutes />},
    {path: 'crm', element: <CrmRoutes />},

    canViewAnalytics && {path: 'audit', element: <AuditRoutes />},
    {
      path: 'analytics',
      element: <AnalyticsRoutes store={reduxStore} />,
    },
    canViewByod && {
      path: 'byod',
      element: <ByodRoutes />,
    },
    {
      path: 'meetings',
      element: <SchedulerRoutes store={reduxStore} />,
    },
    {
      path: 'sense-jd-ai',
      element: <SenseJDAi />,
    },
    {
      path: 'missing-labels-dashboard',
      element: <MissingLabelsContainer />,
    },
    canViewAnalytics && {
      path: 'schedule',
      element: <ScheduleRoutes />,
    },
    canUseReferral && {
      path: 'referral',
      element: <ReferralRoutes />,
      exact: true,
    },
    canViewWorkflows && {
      path: 'journey(s)',
      element: <WorkflowRoutes senseStore={senseStore} />,
    },
    canAccessAgentBuilder && {
      path: 'agents',
      element: <AgentBuilderRoutes />,
    },
    j2Enabled && {
      path: 'sequence-template',
      element: <SequenceTemplateRoutes />,
    },
    j2Enabled &&
      canViewAutomationWorkflows && {
        path: 'automation-workflow(s)',
        element: <AutomationWorkflowRoutes />,
      },
    j2Enabled &&
      canViewAutomationWorkflows && {
        path: 'automation-template(s)',
        element: <AutomationTemplateRoutes />,
      },
    j2Enabled &&
      automationSurveyEnabled &&
      canViewSurveys && {
        path: 'automation-survey(s)',
        element: <AutomationSurveyRoutes />,
      },
    j2Enabled &&
      viewAiRecruiter &&
      !!aiRecruiterV2Enabled && {
        path: 'ai-recruiter-v2',
        element: <AiRecruiterV2Routes />,
      },
    j2Enabled &&
      !!aiRecruiterEnabled &&
      viewAiRecruiter && {
        path: 'ai-recruiter',
        element: <AiRecruiterRoutes />,
      },

    canViewTRM && {
      path: 'trm',
      element: <TrmRoutes senseStore={senseStore} />,
    },
    canEditChatbot && {
      path: 'conversation(s)',
      element: <ChatbotRoutes />,
    },
    askAiEnabled && {
      path: 'askai',
      element: <AskAIRoutes />,
    },
    canViewWorkflows && {
      path: 'database-cleanup(s)',
      element: <DbCleanupRoutes />,
    },
    (canEditReferralV2 || canViewReferralV2 || referralAdminV2) && {
      path: 'referralv2',
      element: <ReferralV2Routes />,
    },
    (Boolean(canViewVoiceflow) || showVoiceFlowMarketingLandingPage) && {
      path: 'voiceflow',
      element: <VoiceflowRoutes />,
    },
    landingPageView && {
      path: 'pages',
      element: <LandingPageRoutes />,
    },
    showSenseEvents && {
      path: 'drive',
      element: <CandidateDriveRoutes />,
    },
    canViewFieldsPage && {
      path: 'fields',
      element: <FieldsRoutes />,
    },
    isTasksEnabled && {
      path: 'tasks',
      element: <TaskAndReminderRoutes store={reduxStore} />,
    },
    isAIReceptionistEnabled &&
      canViewVoiceflow && {
        path: 'ai-receptionist',
        element: <AIReceptionistsRoutes />,
      },
  ];

  const topNavOnlyRoutes = [
    {path: 'style-guide', element: <StyleGuide />},
    {path: 'agentic-ui', element: <AgenticUIPlaygroundRoutes />},
    {path: 'device-not-supported', element: <DeviceNotSupported />},
    {path: '500', element: <Error500 />},
    {
      path: '404',
      element: <NotFound />,
    },
    {path: '403', element: <NotAllowed />},
    {
      path: '*',
      // NOTE (kyle): if they're not logged in, and they hit a 404, ask them to log in.
      element: senseStore.me.isAuthed() ? (
        <NotFound />
      ) : (
        <RequireAuth senseStore={senseStore} />
      ),
    },
  ];

  const appShellRoutes = [
    ...sideNavVisibleRoutes,
    ...sideNavHiddenRoutes,
    ...topNavOnlyRoutes,
  ];

  const [content] = useRoutes(
    isAgencyInMaintenanceMode
      ? [{path: '*', element: <MaintenanceRoutes />}]
      : [
          workflowJourneyRedirect,
          // introduced referrer portal integration user form path
          {
            path: '/u/:referralLinkId',
            element: <ReferrerPortalUserForm />,
          },
          {path: 'uidesign', element: <DesignDoc />},
          {
            element: ({children}) => (
              <AuthenticationContainer
                children={children}
                senseStore={senseStore}
              />
            ),
            children: [
              {
                path: 'signup',
                element: <SignUpContainer />,
              },
              {
                path: 'signin',
                element: <SignInContainer dynamicAgency={dynamicAgency} />,
              },
              {
                path: 'signout',
                element: onEnter(() => void signOut(senseStore)),
              },
              {
                path: 'forgot',
                element: (
                  <ForgotPasswordContainer dynamicAgency={dynamicAgency} />
                ),
              },
              {
                path: 'phone/reset/:token',
                element: <ApprovePhoneReset />,
              },
              {
                path: 'mfa/reset/:token',
                element: <AuthenticatorAppResetApprove />,
              },
              {
                path: 'reset',
                element: <ResetPasswordContainer />,
              },
            ],
          },
          {
            path: 'ats',
            element: <AtsRoutes />,
          },
          {
            path: 'survey',
            element: <SurveyRoutes />,
          },
          {
            path: 'exec-email',
            element: <ExecEmailRoutes />,
          },
          {
            path: 'referral-external',
            element: <ReferralExternalRoutes />,
          },
          {
            path: 'content-subscription/manage',
            element: <ContentSubscriptionRoutes />,
          },
          {
            element: senseStore.me.isAuthed() ? (
              container(AppShell)
            ) : (
              <RequireAuth senseStore={senseStore} />
            ),
            children: !showOnboardPage
              ? [
                  {
                    path: 'onboard',
                    element: redirect(indexRedirectPath),
                  },
                  // @ts-expect-error - TS2769 - No overload matches this call.
                ].concat(appShellRoutes)
              : [
                  {
                    path: 'onboard/task/:taskId/approve/:token',
                    element: <MarkTaskCompletion />,
                  },
                  {
                    path: 'onboard/mark-onboarding-complete',
                    element: <MarkOnboardingCompletion />,
                  },
                  {
                    path: 'onboard',
                    element: <OnboardPage />,
                  },
                  // @ts-expect-error - TS2769 - No overload matches this call.
                ].concat(appShellRoutes),
          },
        ],
  );
  useDelightedRouteSurvey(routeToProjectMap);
  return (
    <RouterErrorBoundary>
      {senseStore.me.isAuthed() ? (
        <ExtensionConnection>{content}</ExtensionConnection>
      ) : (
        <>{content}</>
      )}
    </RouterErrorBoundary>
  );
}

// @ts-expect-error - TS7031 - Binding element 'senseStore' implicitly has an 'any' type.
function RequireAuth({senseStore}) {
  const history = useHistory();
  const {location} = history;

  // TRM uses hashed URLs (e.g. /trm#/overview, /trm/#/overview), while other routes use standard paths.
  // Without this check, users coming from TRM would lose the hash part after auth and land on the trm default page.
  // This ensures users are redirected back to the correct hashed route in TRM after signing in,
  // while keeping behavior unchanged for other routes.
  const trmPaths = ['/trm', '/trm/'];
  const nextPath = trmPaths.includes(location.pathname)
    ? `/trm${location.hash}`
    : location.pathname;

  React.useEffect(() => {
    senseStore.me.setState({
      postAuthPath: nextPath,
      postAuthSearch: location.search,
    });
    history.replace({
      pathname: '/signin',
      query: {
        nextPathname: nextPath,
        nextSearch: location.search,
      },
    });
  }, []);

  return null;
}
